import Backoffice from "@snark/backoffice"
import StoreCroppedFilesField from "../components/StoreCroppedFilesField.vue"
import Vue from "vue"

// noinspection JSUnusedLocalSymbols
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};

const context = {
    account: Backoffice.populator("account", {}),
    building: Backoffice.populator("building", {
    }),
};

async function getAccountOptions() {
    const accounts = await Vue.backoffice.requestor.get("/account");
    return (Array.isArray(accounts) ? accounts : [])
        .map(account => ({
            value: account._id,
            label: account.name || account._id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

async function getBuildingOptions(input) {
    const accountId = typeof input === "string" ? input : input?.object?.accountId;
    if (!accountId) return [];

    const buildings = await Vue.backoffice.requestor.get("/building", null, { accountId });
    return (Array.isArray(buildings) ? buildings : [])
        .filter(building => building.accountId?.toString?.() === accountId.toString())
        .map(building => ({
            value: building._id,
            label: building.name || building._id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
}


export default {
    name: "store",
    collection: "store",
    idProperty: "_id",
    section: "app",
    backPopulateList: ["account", "building"],
    labels: { singleTitle: "Store", listTitle: "Store" },
    emptyItem: { accountId: null, buildingId: null, name: "", description: "", tags: [], isFree: false, isGrantedToBuilding: false, files: [] },

    fields: [
        {
            name: "_id",
            label: "Id",
            readOnly: true,
            hideOnCreate: true,
        },
        {
            name: "accountId",
            label: "Account Id",
            formatter: Backoffice.formatters.linkObjectId("account", {
                idProperty: "_id",
                label: context.account.formatter(),
            }),
            editor: {
                type: "select",
                options: getAccountOptions,
                transform: (value, form) => {
                    if (form.accountId !== value) {
                        form.buildingId = null;
                        form.files = [];
                    }
                    return value;
                },
            },
        },
        {
            name: "buildingId",
            label: "Building Id",
            formatter: Backoffice.formatters.linkObjectId("building", {
                idProperty: "_id",
                label: context.building.formatter(),
            }),
            editor: {
                type: "select",
                options: getBuildingOptions,
                watch: "accountId",
                transform: (value, form) => {
                    if (form.buildingId !== value) {
                        form.files = [];
                    }
                    return value;
                },
            },
        },
        {
            name: "name",
            label: "Name",
            editor: { type: "string" },
        },
        {
            name: "description",
            label: "Description",
            editor: { type: "textarea" },
        },
        {
            name: "tags",
            label: "Tags",
            editor: {
                type: "tags",
                valueFormat: "array",
                textKey: "text",
                tags: [],
                formatter: Backoffice.formatters.tags(undefined, " ", (property, item) => {
                    const value = item[property.name];
                    if (typeof value === "string") return value;
                    return value && value.text ? value.text : "";
                }),
                allowCustomTag: true,
                getText: (tag) => {
                    if (typeof tag === "string") return tag;
                    return tag && tag.text ? tag.text : "";
                },
                setText: (tag, text) => {
                    if (typeof tag === "string") return text;
                    if (tag && typeof tag === "object") {
                        tag.text = text;
                        return tag;
                    }
                    return { text };
                },
            },
        },
        {
            name: "isFree",
            label: "Is Free",
            formatter: Backoffice.formatters.boolean(["No", "Yes"]),
            editor: { type: "boolean", labels: ["No", "Yes"] },
        },
        {
            name: "isGrantedToBuilding",
            label: "Allow this building to add it",
            formatter: Backoffice.formatters.boolean(["No", "Yes"]),
            editor: { type: "boolean", labels: ["No", "Yes"] },
        },
        {
            name: "video",
            label: "Source media",
            formatter: (property, item) => {
                const value = item[property.name];
                if (!value) return "No media";
                const url = value.presignedUrl || value.path;
                if (!url) return "No media";
                const style = 'max-width:400px;max-height:300px;';
                if (value.mimetype && value.mimetype.startsWith("image/")) {
                    return `<img alt="" src="${url}" style="${style}" crossorigin="anonymous"/>`;
                }
                return `<video controls style="${style}" crossorigin="anonymous"><source src="${url}" type="video/mp4">Your browser does not support the video tag.</video>`;
            },
            editor: {
                type: "file",
                accept: "video/*,image/*",
                uploadAction: {
                    url: "/store/:storeId",
                    method: "post",
                    encodeFormData(formData, form) {
                        formData.append("tags", JSON.stringify(form.tags || []));
                        formData.append("name", form.name || "");
                        formData.append("description", form.description || "");
                        formData.append("isFree", form.isFree ? "true" : "false");
                        formData.append("isGrantedToBuilding", form.isGrantedToBuilding ? "true" : "false");
                        formData.append("files", JSON.stringify(form.files || []));
                        return formData;
                    }
                },
                preview: {
                    urlPrefix: "",
                    contain: true,
                },
                style: { cursor: "pointer" },
            },
        },
        {
            name: "files",
            label: "Store playback files",
            formatter: (_property, item) => Array.isArray(item.files) ? `${item.files.length} file(s)` : "",
            editor: {
                type: "custom",
                component: StoreCroppedFilesField,
            },
        },
    ],
    listColumns: [
        {
            name: "name",
            label: "Name",
        },
        {
            name: "picture",
            label: "Preview",
            formatter: Backoffice.formatters.picture({
                urlPrefix: "",
                maxWidth: 220,
                maxHeight: 140,
            }),
        },
        {
            name: "isFree",
            label: "Free or Paid",
            formatter: Backoffice.formatters.boolean(["Paid", "Free"]),
        },
        {
            name: "isGrantedToBuilding",
            label: "Granted",
            formatter: Backoffice.formatters.boolean(["No", "Yes"]),
        },
        {
            name: "account",
            label: "Account",
            formatter: Backoffice.formatters.subProperty('name'),
            nosingle: true,
        },
        {
            name: "building",
            label: "Building",
            formatter: Backoffice.formatters.subProperty('name'),
            nosingle: true,
        },
    ],
    async loadSingle() {
        await Promise.all([
            context.account.load(),
            context.building.load(),
        ]);
    },
};
